import { useState } from 'react';
import {
  Search,
  Plus,
  Swords,
  Eye,
  Edit,
  Trash2,
  Globe,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type ThreatLevel = 'low' | 'medium' | 'high';

interface Competitor {
  id: string;
  name: string;
  website: string;
  description: string;
  threatLevel: ThreatLevel;
  strengths: string[];
  weaknesses: string[];
  dealsLost: number;
  dealsWon: number;
}

// Mock data
const MOCK_COMPETITORS: Competitor[] = [
  {
    id: '1',
    name: 'Jack Henry & Associates',
    website: 'jackhenry.com',
    description: 'Major core processor with LOS solutions for community banks',
    threatLevel: 'high',
    strengths: ['Large market presence', 'Integrated solutions', 'Strong brand'],
    weaknesses: ['Higher pricing', 'Complex implementation'],
    dealsLost: 3,
    dealsWon: 2,
  },
  {
    id: '2',
    name: 'Fiserv',
    website: 'fiserv.com',
    description: 'Enterprise-level financial technology provider',
    threatLevel: 'high',
    strengths: ['Enterprise scale', 'Full banking suite', 'Global presence'],
    weaknesses: ['Less flexible', 'Long contracts'],
    dealsLost: 2,
    dealsWon: 1,
  },
  {
    id: '3',
    name: 'nCino',
    website: 'ncino.com',
    description: 'Cloud banking platform focused on loan origination',
    threatLevel: 'medium',
    strengths: ['Modern cloud platform', 'Salesforce integration', 'Good UX'],
    weaknesses: ['Salesforce dependency', 'Higher cost for small FIs'],
    dealsLost: 1,
    dealsWon: 3,
  },
  {
    id: '4',
    name: 'Abrigo',
    website: 'abrigo.com',
    description: 'Risk management and lending solutions for CFIs',
    threatLevel: 'medium',
    strengths: ['Risk focus', 'Credit union expertise', 'Compliance tools'],
    weaknesses: ['Dated interface', 'Limited automation'],
    dealsLost: 1,
    dealsWon: 2,
  },
  {
    id: '5',
    name: 'Finastra',
    website: 'finastra.com',
    description: 'Global financial software provider',
    threatLevel: 'low',
    strengths: ['Global reach', 'Large portfolio'],
    weaknesses: ['Less CFI focus', 'Complex pricing'],
    dealsLost: 0,
    dealsWon: 4,
  },
];

const THREAT_BADGES: Record<ThreatLevel, { label: string; className: string }> = {
  low: { label: 'Low', className: 'badge-secondary' },
  medium: { label: 'Medium', className: 'badge-warning' },
  high: { label: 'High', className: 'bg-rust/10 text-rust dark:bg-salmon/20 dark:text-salmon' },
};

export function CompetitorList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [threatFilter, setThreatFilter] = useState<ThreatLevel | 'all'>('all');

  // Filter competitors
  let filteredCompetitors = MOCK_COMPETITORS;

  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredCompetitors = filteredCompetitors.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query)
    );
  }

  if (threatFilter !== 'all') {
    filteredCompetitors = filteredCompetitors.filter((c) => c.threatLevel === threatFilter);
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate dark:text-mist">
            Competitors
          </h1>
          <p className="text-chalkboard dark:text-rain-cloud mt-1">
            Track competitors and analyze win/loss patterns
          </p>
        </div>
        <button className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Competitor
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-french-gray" />
              <input
                type="text"
                placeholder="Search competitors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>
            <select
              value={threatFilter}
              onChange={(e) => setThreatFilter(e.target.value as ThreatLevel | 'all')}
              className="select w-full sm:w-48"
            >
              <option value="all">All Threat Levels</option>
              <option value="high">High Threat</option>
              <option value="medium">Medium Threat</option>
              <option value="low">Low Threat</option>
            </select>
          </div>
        </div>
      </div>

      {/* Competitors Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredCompetitors.map((competitor) => {
          const threatBadge = THREAT_BADGES[competitor.threatLevel];
          const winRate = competitor.dealsWon + competitor.dealsLost > 0
            ? Math.round((competitor.dealsWon / (competitor.dealsWon + competitor.dealsLost)) * 100)
            : 0;

          return (
            <div key={competitor.id} className="card">
              <div className="card-body">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
                      <Swords className="w-5 h-5 text-pond dark:text-pond-light" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate dark:text-mist">
                        {competitor.name}
                      </h3>
                      <a 
                        href={`https://${competitor.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-pond dark:text-pond-light hover:underline flex items-center gap-1"
                      >
                        <Globe className="w-3 h-3" />
                        {competitor.website}
                      </a>
                    </div>
                  </div>
                  <span className={cn('badge', threatBadge.className)}>
                    {threatBadge.label} Threat
                  </span>
                </div>

                <p className="text-sm text-chalkboard dark:text-rain-cloud mb-4">
                  {competitor.description}
                </p>

                {/* Win/Loss Stats */}
                <div className="flex items-center gap-4 mb-4 py-3 border-y border-gray dark:border-night-pond">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-turquoise dark:text-aqua" />
                    <span className="text-sm">
                      <span className="font-medium text-slate dark:text-mist">{competitor.dealsWon}</span>
                      <span className="text-chalkboard dark:text-rain-cloud"> won</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-rust dark:text-salmon" />
                    <span className="text-sm">
                      <span className="font-medium text-slate dark:text-mist">{competitor.dealsLost}</span>
                      <span className="text-chalkboard dark:text-rain-cloud"> lost</span>
                    </span>
                  </div>
                  <div className="ml-auto text-sm">
                    <span className="text-chalkboard dark:text-rain-cloud">Win rate: </span>
                    <span className={cn(
                      'font-medium',
                      winRate >= 50 ? 'text-turquoise dark:text-aqua' : 'text-rust dark:text-salmon'
                    )}>
                      {winRate}%
                    </span>
                  </div>
                </div>

                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-turquoise dark:text-aqua mb-1">Strengths</p>
                    <ul className="space-y-1">
                      {competitor.strengths.map((s, i) => (
                        <li key={i} className="text-chalkboard dark:text-rain-cloud">• {s}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-rust dark:text-salmon mb-1">Weaknesses</p>
                    <ul className="space-y-1">
                      {competitor.weaknesses.map((w, i) => (
                        <li key={i} className="text-chalkboard dark:text-rain-cloud">• {w}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray dark:border-night-pond">
                  <button className="btn-ghost btn-sm">
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                  <button className="btn-ghost btn-sm">
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button className="btn-ghost btn-sm text-rust dark:text-salmon">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredCompetitors.length === 0 && (
        <div className="card">
          <div className="p-8 text-center">
            <p className="text-chalkboard dark:text-rain-cloud">No competitors found.</p>
          </div>
        </div>
      )}
    </div>
  );
}
